import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Calculate, Person, LocationOn, AccessTime } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useForm, Controller } from 'react-hook-form';
import { useChart } from '../contexts/ChartContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import AuthCaptcha from '../components/forms/AuthCaptcha';
import CitySearch from '../components/forms/CitySearch';
import { KundliChartStyle, NodeMode, useUserPreferences } from '../contexts/UserPreferencesContext';
import { useTranslation } from 'react-i18next';
import { getLanguagePreferenceName, LANGUAGE_OPTIONS } from '../i18n/languages';

interface ChartFormData {
  name: string;
  question: string;
  seedNumber: number;
  dateOfBirth: Date | null;
  timeOfBirth: Date | null;
  placeOfBirth: any;
  useCustomCoordinates: boolean;
  customLatitude: string;
  customLongitude: string;
  customTimezone: string;
  gender: string;
  saveChart: boolean;
  chartStyle: string;
  language: string;
  nodeMode: NodeMode;
}

const ANONYMOUS_FIRST_CHART_PROMPT_KEY = 'poojaastro_anonymous_first_chart_prompt_seen';

type ActivePrompt = 'saveLimit' | 'registration' | null;

const CHART_STYLE_OPTIONS: { value: string; preference: KundliChartStyle; label: string }[] = [
  { value: 'North', preference: 'north', label: 'उत्तर भारतीय / North Indian' },
  { value: 'South', preference: 'south', label: 'दक्षिण भारतीय / South Indian' },
  { value: 'East', preference: 'east', label: 'पूर्व भारतीय / East Indian' },
];

const toChartStylePreference = (value: string): KundliChartStyle => {
  const option = CHART_STYLE_OPTIONS.find((item) => item.value === value);
  return option?.preference || 'north';
};

const toChartStyleFormValue = (value: KundliChartStyle) => {
  const option = CHART_STYLE_OPTIONS.find((item) => item.preference === value);
  return option?.value || 'North';
};

const generateCaptchaChallenge = (length: number = 6) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const formatDateForDisplay = (value: Date | string | null) => {
  if (!value) return '';
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const date = new Date(value);
      if (!Number.isNaN(date.getTime())) {
        return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      }
    }
    return value;
  }
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
};

const formatDateInput = (value: string) => {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const parseDisplayDate = (value: string) => {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
};

const formatDateForApi = (date: Date) => (
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
);

const parseCoordinate = (value: string, min: number, max: number) => {
  const normalized = value.trim();
  if (!normalized) return null;
  const coordinate = Number(normalized);
  if (!Number.isFinite(coordinate) || coordinate < min || coordinate > max) return null;
  return coordinate;
};

const parseTimezoneOffset = (value: string) => {
  const normalized = value.trim();
  if (!normalized) return null;

  const colonMatch = normalized.match(/^([+-])(\d{1,2}):(\d{2})$/);
  if (colonMatch) {
    const sign = colonMatch[1] === '-' ? -1 : 1;
    const hours = Number(colonMatch[2]);
    const minutes = Number(colonMatch[3]);
    const total = sign * ((hours * 60) + minutes);
    return hours <= 14 && minutes <= 59 && total >= -720 && total <= 720 ? total : null;
  }

  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return null;
  return numeric >= -720 && numeric <= 720 ? numeric : null;
};

const ChartCalculation: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { state: chartState, calculateChart, calculateHoraryChart } = useChart();
  const { state: authState } = useAuth();
  const { chartStyle, setChartStyle, nodeMode, setNodeMode } = useUserPreferences();
  // Every signed-in user can save charts; the backend enforces the free-tier
  // allowance (10) and leaves paid plans unlimited.
  const canSaveChart = authState.isAuthenticated;
  const defaultLocalTime = React.useMemo(() => new Date(), []);
  

  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [chartMode, setChartMode] = useState<'birth' | 'horary'>('birth');
  const [captchaChallenge, setCaptchaChallenge] = useState(generateCaptchaChallenge());
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState('');
  const [activePrompt, setActivePrompt] = useState<ActivePrompt>(null);
  const [pendingSubmission, setPendingSubmission] = useState<ChartFormData | null>(null);

  // Set page-specific document title
  useEffect(() => {
    document.title = location.pathname === '/horary'
      ? 'Horary Calculation - Kundlistar - Nadi, KP, Vedic, Jaimini, Numerology Software'
      : 'Chart Calculation - Kundlistar - Nadi, KP, Vedic, Jaimini, Numerology Software';
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname === '/horary') {
      setChartMode('horary');
    } else {
      setChartMode('birth');
    }
  }, [location.pathname]);

  const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<ChartFormData>({
    defaultValues: {
      name: '',
      question: '',
      seedNumber: 1,
      dateOfBirth: defaultLocalTime,
      timeOfBirth: defaultLocalTime,
      placeOfBirth: null,
      useCustomCoordinates: false,
      customLatitude: '',
      customLongitude: '',
      customTimezone: '+05:30',
      gender: 'Male',
      saveChart: true,
      chartStyle: toChartStyleFormValue(chartStyle),
      language: getLanguagePreferenceName(i18n.resolvedLanguage || i18n.language),
      nodeMode
    }
  });

  useEffect(() => {
    setValue('chartStyle', toChartStyleFormValue(chartStyle));
  }, [chartStyle, setValue]);

  useEffect(() => {
    setValue('nodeMode', nodeMode);
  }, [nodeMode, setValue]);

  useEffect(() => {
    setValue('language', getLanguagePreferenceName(i18n.resolvedLanguage || i18n.language));
  }, [i18n.language, i18n.resolvedLanguage, setValue]);

  const refreshCaptcha = (clearError: boolean = true) => {
    setCaptchaChallenge(generateCaptchaChallenge());
    setCaptchaAnswer('');
    if (clearError) {
      setCaptchaError('');
    }
  };
  const isChartLimitError = (message: string) =>
    /chart limit exceeded|upgrade your subscription/i.test(message);

  const navigateToChart = (chartId?: string) => {
    if (chartId) {
      navigate(`/chart/${chartId}`);
      return;
    }

    navigate('/chart/preview');
  };

  const maybeShowPostChartPrompt = () => {
    if (!authState.isAuthenticated) {
      const hasSeenRegistrationPrompt = window.localStorage.getItem(ANONYMOUS_FIRST_CHART_PROMPT_KEY);
      if (!hasSeenRegistrationPrompt) {
        window.localStorage.setItem(ANONYMOUS_FIRST_CHART_PROMPT_KEY, 'true');
        setActivePrompt('registration');
        return true;
      }
      return false;
    }

    return false;
  };

  const runCalculation = async (data: ChartFormData, forceUnsaved: boolean = false) => {
    setChartStyle(toChartStylePreference(data.chartStyle));
    setNodeMode(data.nodeMode);
    const shouldSaveChart = forceUnsaved ? false : data.saveChart;
    const shouldSaveAnonymized = forceUnsaved;
    const selectedDate = typeof data.dateOfBirth === 'string'
      ? parseDisplayDate(data.dateOfBirth)
      : (data.dateOfBirth || new Date());

    if (!selectedDate || Number.isNaN(selectedDate.getTime())) {
      setCalculationError('कृपया तारीख DD/MM/YYYY फॉर्मेट में दर्ज करें / Please enter date in DD/MM/YYYY format');
      return;
    }

    const customLatitude = parseCoordinate(data.customLatitude || '', -90, 90);
    const customLongitude = parseCoordinate(data.customLongitude || '', -180, 180);
    const customTimezone = parseTimezoneOffset(data.customTimezone || '');
    const locationPayload = data.useCustomCoordinates
      ? {
          city: 'Custom Location',
          state: '',
          country: 'Custom',
          latitude: customLatitude as number,
          longitude: customLongitude as number,
          timezone: customTimezone as number
        }
      : {
          city: data.placeOfBirth.name,
          state: data.placeOfBirth.admin1?.name || '',
          country: data.placeOfBirth.country.name,
          latitude: data.placeOfBirth.coordinates.latitude,
          longitude: data.placeOfBirth.coordinates.longitude,
          timezone: data.placeOfBirth.timezone?.offset || 330
        };

    if (chartMode === 'horary') {
      if (!Number(data.seedNumber) || Number(data.seedNumber) < 1 || Number(data.seedNumber) > 249) {
        setCalculationError('Seed number 1 से 249 के बीच होना चाहिए / Seed number must be between 1 and 249');
        return;
      }

      const horaryChart = await calculateHoraryChart({
        name: data.name || `Horary Query ${data.seedNumber}`,
        question: data.question,
        seedNumber: Number(data.seedNumber),
        dateOfQuery: formatDateForApi(selectedDate),
        timeOfQuery: {
          hour: data.timeOfBirth!.getHours(),
          minute: data.timeOfBirth!.getMinutes(),
          second: data.timeOfBirth!.getSeconds()
        },
        placeOfQuery: locationPayload,
        saveChart: shouldSaveChart,
        saveAnonymized: shouldSaveAnonymized,
        chartSettings: {
          chartStyle: data.chartStyle,
          language: data.language,
          nodeMode: data.nodeMode
        }
      });

      return horaryChart?._id || null;
    }

    const chart = await calculateChart({
      name: data.name,
      dateOfBirth: formatDateForApi(selectedDate),
      timeOfBirth: {
        hour: data.timeOfBirth!.getHours(),
        minute: data.timeOfBirth!.getMinutes(),
        second: data.timeOfBirth!.getSeconds()
      },
      placeOfBirth: locationPayload,
      gender: data.gender,
      saveChart: shouldSaveChart,
      saveAnonymized: shouldSaveAnonymized,
      chartSettings: {
        chartStyle: data.chartStyle,
        language: data.language,
        nodeMode: data.nodeMode
      }
    });

    return chart?._id || null;
  };

  const handleContinueWithoutSaving = async () => {
    if (!pendingSubmission) {
      setActivePrompt(null);
      return;
    }

    try {
      setActivePrompt(null);
      const chartId = await runCalculation(pendingSubmission, true);
      setPendingSubmission(null);
      if (!maybeShowPostChartPrompt()) {
        navigateToChart(chartId || undefined);
      }
    } catch (error: any) {
      setCalculationError(error.message || 'Calculation failed');
    }
  };

  const handleGoPremium = () => {
    setActivePrompt(null);
    navigate('/upgrade');
  };

  const handleCreateFreeAccount = () => {
    setActivePrompt(null);
    navigate('/register');
  };

  const handleContinueToResults = () => {
    setActivePrompt(null);
    const currentChartId = chartState.currentChart?._id || chartState.currentChart?.chartId;
    navigateToChart(currentChartId);
  };

  // Handle form submission
  const onSubmit = async (data: ChartFormData) => {
    try {
      setCalculationError(null);
      setCaptchaError('');

      if (!authState.isAuthenticated && captchaAnswer.trim().toUpperCase() !== captchaChallenge) {
        setCaptchaError('Captcha does not match. Please try again.');
        refreshCaptcha(false);
        return;
      }

      if (!data.timeOfBirth || (!data.useCustomCoordinates && !data.placeOfBirth)) {
        setCalculationError('कृपया सभी आवश्यक जानकारी भरें / Please fill all required information');
        return;
      }

      if (data.useCustomCoordinates) {
        const latitude = parseCoordinate(data.customLatitude || '', -90, 90);
        const longitude = parseCoordinate(data.customLongitude || '', -180, 180);
        const timezone = parseTimezoneOffset(data.customTimezone || '');
        if (latitude === null || longitude === null || timezone === null) {
          setCalculationError('कृपया valid latitude (-90 to 90), longitude (-180 to 180), और timezone (+05:30/-04:00) दर्ज करें / Please enter valid custom coordinates and timezone');
          return;
        }
      }

      if (canSaveChart && authState.isAuthenticated && data.saveChart && authState.user && !authState.user.canCreateChart) {
        setPendingSubmission(data);
        setActivePrompt('saveLimit');
        return;
      }

      const chartId = await runCalculation(data);
      if (!maybeShowPostChartPrompt()) {
        navigateToChart(chartId || undefined);
      }
    } catch (error: any) {
      const message = error.message || 'Calculation failed';
      if (canSaveChart && authState.isAuthenticated && data.saveChart && isChartLimitError(message)) {
        setPendingSubmission(data);
        setActivePrompt('saveLimit');
        return;
      }

      setCalculationError(error.message);
      if (!authState.isAuthenticated) {
        refreshCaptcha();
      }
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <Paper sx={{ p: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          
          <Typography variant="h3" component="h1" gutterBottom>
            चार्ट कैलकुलेशन / Chart Calculation
          </Typography>
          
        </Box>

        <Box sx={{ mb: 3 }}>
          <Tabs
            value={chartMode}
            onChange={(_, value) => {
              setChartMode(value);
              if (value === 'horary') {
                navigate('/horary');
                if (!watch('dateOfBirth')) setValue('dateOfBirth', new Date());
                if (!watch('timeOfBirth')) setValue('timeOfBirth', new Date());
              } else {
                navigate('/calculate');
              }
            }}
            centered
          >
            <Tab value="birth" label="Birth Chart" />
            <Tab value="horary" label="Horary / Prashna" />
          </Tabs>
        </Box>

        {chartMode === 'horary' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            KP Horary में 1 से 249 तक का Seed Number उपयोग करें। ग्रह query के current time और location पर calculate होंगे।
          </Alert>
        )}



        {/* Error Display */}
        {calculationError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {calculationError}
          </Alert>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                {/* Personal Info Section */}
                <Grid size={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                    {chartMode === 'horary' ? 'प्रश्न जानकारी / Horary Query Details' : 'व्यक्तिगत जानकारी / Personal Info'}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label={chartMode === 'horary' ? 'Query Title / प्रश्न शीर्षक' : 'पूरा नाम / Full Name (optional)'}
                        placeholder={chartMode === 'birth' ? 'Optional' : undefined}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        InputProps={{
                          startAdornment: <Person sx={{ mr: 1, color: 'action.active' }} />
                        }}
                      />
                    )}
                  />
                </Grid>

                {chartMode === 'horary' && (
                  <>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Controller
                        name="question"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="Question / सवाल"
                            placeholder="What do you want to ask?"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Controller
                        name="seedNumber"
                        control={control}
                        rules={{
                          required: 'Seed number is required',
                          min: { value: 1, message: 'Minimum seed is 1' },
                          max: { value: 249, message: 'Maximum seed is 249' }
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            type="number"
                            fullWidth
                            label="Seed Number (1-249)"
                            error={!!errors.seedNumber}
                            helperText={errors.seedNumber?.message as string}
                            inputProps={{ min: 1, max: 249 }}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name="dateOfBirth"
                    control={control}
                    rules={{ required: 'जन्म तिथि आवश्यक है / Birth date is required' }}
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TextField
                          label={chartMode === 'horary' ? 'प्रश्न तिथि / Query Date (DD/MM/YYYY)' : 'जन्म तिथि / Birth Date (DD/MM/YYYY)'}
                          fullWidth
                          value={formatDateForDisplay(field.value)}
                          onChange={(e) => field.onChange(formatDateInput(e.target.value))}
                          placeholder="DD/MM/YYYY"
                          error={!!errors.dateOfBirth}
                          helperText={errors.dateOfBirth?.message as string}
                          inputProps={{ maxLength: 10 }}
                        />
                      </LocalizationProvider>
                    )}
                  />  
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name="timeOfBirth"
                    control={control}
                    rules={{ required: 'जन्म समय आवश्यक है / Birth time is required' }}
                    render={({ field }) => (
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TimePicker
                          label={chartMode === 'horary' ? 'प्रश्न समय / Query Time' : 'जन्म समय / Birth Time'}
                          value={field.value}
                          onChange={(time) => field.onChange(time)}
                          views={['hours', 'minutes', 'seconds']}
                          format="HH:mm:ss"
                          ampm={false}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.timeOfBirth,
                              helperText: errors.timeOfBirth?.message,
                              InputProps: {
                                startAdornment: <AccessTime sx={{ mr: 1, color: 'action.active' }} />
                              }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    )}
                  />
                </Grid>

                {chartMode === 'birth' && (
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth>
                          <InputLabel>लिंग / Gender</InputLabel>
                          <Select {...field} label="लिंग / Gender">
                            <MenuItem value="Male">पुरुष / Male</MenuItem>
                            <MenuItem value="Female">स्त्री / Female</MenuItem>
                            <MenuItem value="Other">अन्य / Other</MenuItem>
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                )}

                {/* Location Section */}
                <Grid size={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2, mb: 2 }}>
                    {chartMode === 'horary' ? 'प्रश्न स्थान / Query Location' : 'स्थान चुनें / Location'}
                  </Typography>
                </Grid>

                <Grid size={12}>
                  <Controller
                    name="useCustomCoordinates"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={field.value}
                            onChange={(event) => {
                              field.onChange(event.target.checked);
                              if (event.target.checked) {
                                setValue('placeOfBirth', null);
                              }
                            }}
                          />
                        }
                        label="Custom latitude/longitude use करें / Use custom latitude and longitude"
                      />
                    )}
                  />
                </Grid>

                {!watch('useCustomCoordinates') ? (
                  <Grid size={12}>
                    <Controller
                      name="placeOfBirth"
                      control={control}
                      rules={{
                        validate: (value) => watch('useCustomCoordinates') || !!value || 'जन्म स्थान आवश्यक है / Birth place is required'
                      }}
                      render={({ field }) => (
                        <CitySearch
                          value={field.value}
                          onChange={field.onChange}
                          label={chartMode === 'horary' ? 'प्रश्न स्थान / Query Place' : 'जन्म स्थान / Birth Place'}
                          placeholder="शहर खोजें / Search city..."
                          error={!!errors.placeOfBirth}
                          helperText={errors.placeOfBirth?.message?.toString()}
                        />
                      )}
                    />
                  </Grid>
                ) : (
                  <>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="customLatitude"
                        control={control}
                        rules={{
                          validate: (value) => parseCoordinate(value || '', -90, 90) !== null || 'Latitude -90 और 90 के बीच होना चाहिए'
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="अक्षांश / Latitude"
                            placeholder="28.6139"
                            error={!!errors.customLatitude}
                            helperText={errors.customLatitude?.message}
                            inputProps={{ inputMode: 'decimal' }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="customLongitude"
                        control={control}
                        rules={{
                          validate: (value) => parseCoordinate(value || '', -180, 180) !== null || 'Longitude -180 और 180 के बीच होना चाहिए'
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="देशांतर / Longitude"
                            placeholder="77.2090"
                            error={!!errors.customLongitude}
                            helperText={errors.customLongitude?.message}
                            inputProps={{ inputMode: 'decimal' }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Controller
                        name="customTimezone"
                        control={control}
                        rules={{
                          validate: (value) => parseTimezoneOffset(value || '') !== null || 'Timezone +05:30, -04:00 या minutes में दर्ज करें'
                        }}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            fullWidth
                            label="समय क्षेत्र / Timezone Offset"
                            placeholder="+05:30"
                            error={!!errors.customTimezone}
                            helperText={errors.customTimezone?.message || 'Use +05:30, -04:00, or minutes like 330'}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}

                {watch('placeOfBirth') && !watch('useCustomCoordinates') && (
                  <Grid size={12}>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {chartMode === 'horary' ? 'प्रश्न स्थान की जानकारी / Query Location Details:' : 'चयनित स्थान की जानकारी / Selected Location Details:'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>स्थान / Location:</strong> {watch('placeOfBirth')?.displayName}
                      </Typography>
                      <Typography variant="body2">
                        <strong>अक्षांश / Latitude:</strong> {watch('placeOfBirth')?.coordinates.latitude.toFixed(4)}°
                      </Typography>
                      <Typography variant="body2">
                        <strong>देशांतर / Longitude:</strong> {watch('placeOfBirth')?.coordinates.longitude.toFixed(4)}°
                      </Typography>
                      <Typography variant="body2">
                        <strong>समय क्षेत्र / Timezone:</strong> {watch('placeOfBirth')?.timezone?.name || 'Asia/Kolkata'}
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {watch('useCustomCoordinates') && (
                  <Grid size={12}>
                    <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Custom Location Details:
                      </Typography>
                      <Typography variant="body2">
                        <strong>Latitude:</strong> {watch('customLatitude') || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Longitude:</strong> {watch('customLongitude') || '-'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Timezone:</strong> {watch('customTimezone') || '-'} ({parseTimezoneOffset(watch('customTimezone') || '') ?? '-'} minutes)
                      </Typography>
                    </Paper>
                  </Grid>
                )}

                {/* Chart Settings Section */}
                <Grid size={12}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mt: 2, mb: 2 }}>
                    चार्ट सेटिंग्स / Chart Settings
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: -1, mb: 2 }}>
                    आप इसे बाद में Settings से बदल सकते हैं। / You can change this later from Settings.
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name="chartStyle"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>चार्ट स्टाइल / Chart Style</InputLabel>
                        <Select {...field} label="चार्ट स्टाइल / Chart Style">
                          {CHART_STYLE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name="language"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>भाषा / Language</InputLabel>
                        <Select {...field} label="भाषा / Language">
                          {LANGUAGE_OPTIONS.map((option) => (
                            <MenuItem key={option.code} value={option.preferenceName}>{option.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                  <Controller
                    name="nodeMode"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>राहु/केतु / Nodes</InputLabel>
                        <Select {...field} label="राहु/केतु / Nodes">
                          <MenuItem value="true">ट्रू नोड / True Node</MenuItem>
                          <MenuItem value="mean">मीन नोड / Mean Node</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  {canSaveChart ? (
                    <Controller
                      name="saveChart"
                      control={control}
                      render={({ field }) => (
                        <Box>
                          <Typography variant="body2" gutterBottom>
                            चार्ट सेव करने के विकल्प / Chart Saving Options:
                          </Typography>
                          <FormControl component="fieldset">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <input
                                type="checkbox"
                                name={field.name}
                                ref={field.ref}
                                onBlur={field.onBlur}
                                checked={field.value}
                                onChange={(e) => field.onChange(e.target.checked)}
                              />
                              <Typography variant="body2">
                                चार्ट को सेव करें और बाद में access करें / Save chart for later access
                              </Typography>
                            </Box>
                          </FormControl>
                          {authState.user && !authState.user.canCreateChart && (
                            <Alert
                              severity="warning"
                              sx={{ mt: 2 }}
                              action={
                                <Button color="inherit" size="small" component={RouterLink} to="/upgrade">
                                  Upgrade
                                </Button>
                              }
                            >
                              आपका chart save access समाप्त हो गया है। Plan चुनें।
                              Your chart save access is inactive. Please choose a plan.
                            </Alert>
                          )}
                        </Box>
                      )}
                    />
                  ) : authState.isAuthenticated ? (
                    <Alert
                      severity="info"
                      action={
                        <Button color="inherit" size="small" component={RouterLink} to="/pricing">
                          View Plans
                        </Button>
                      }
                    >
                      You can calculate without saving. Upgrade to Premium to save charts.
                    </Alert>
                  ) : (
                    <Box>
                      
                      <AuthCaptcha
                        challengeText={captchaChallenge}
                        value={captchaAnswer}
                        error={captchaError}
                        onChange={(value) => {
                          setCaptchaAnswer(value);
                          if (captchaError) {
                            setCaptchaError('');
                          }
                        }}
                        onRefresh={refreshCaptcha}
                      />
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={chartState.isLoading ? <CircularProgress size={20} /> : <Calculate />}
              disabled={chartState.isLoading || (!authState.isAuthenticated && !captchaAnswer.trim())}
              size="large"
            >
              {chartState.isLoading ? 'कैलकुलेट हो रहा है...' : (chartMode === 'horary' ? 'होररी चार्ट कैलकुलेट करें / Calculate Horary Chart' : 'चार्ट कैलकुलेट करें / Calculate Chart')}
            </Button>
          </Box>
        </form>

        <Dialog
          open={activePrompt !== null}
          onClose={() => setActivePrompt(null)}
          fullWidth
          maxWidth="xs">
          {activePrompt === 'saveLimit' && (
            <>
              <DialogTitle sx={{ fontWeight: 600 }}>Plan Required to Save</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
                  You can keep calculating without login. Saving charts is available after upgrading to Premium.
                </Typography>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    border: '1px solid #ffe08a',
                    backgroundColor: '#fff3cd',
                    color: '#7a4e00',
                    borderRadius: 99,
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: 13,
                  }}>
                  Yearly Premium: Rs 999/year
                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={handleContinueWithoutSaving} variant="outlined">
                  Continue Without Saving
                </Button>
                <Button onClick={handleGoPremium} variant="contained">
                  Choose Plan
                </Button>
              </DialogActions>
            </>
          )}

          {activePrompt === 'registration' && (
            <>
              <DialogTitle sx={{ fontWeight: 600 }}>Chart Ready</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                  You can continue using the chart now. Saving charts is available after upgrading to Premium.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2.5 }}>
                <Button onClick={handleContinueToResults} variant="outlined">
                  Continue Without Saving
                </Button>
                <Button onClick={handleCreateFreeAccount} variant="contained">
                  Create Account
                </Button>
              </DialogActions>
            </>
          )}

        </Dialog>
      </Paper>
    </Container>
  );
};

export default ChartCalculation;
